import { UserId } from './uuid.model';

export interface Revision {
  createdAt: string;
  updatedAt: string | null;
  createdBy: UserId;
  updatedBy: UserId | null;
  _version: string;
}

/**
 * Audit trail mixin for domain models — Date-typed timestamps + author fields.
 * `createdBy`/`updatedBy` are {@link UserId} — the UUID of the user who performed the action.
 * Use when the entity has audit fields but no `_version` (e.g. ApplicationStatus).
 * Use {@link ViewRevision} when `_version` is also present.
 */
export interface Auditable {
  createdAt: Date;
  updatedAt: Date | null;
  createdBy: UserId;
  updatedBy: UserId | null;
}

/**
 * Domain-layer counterpart of {@link Revision}.
 * Extends {@link Auditable} and adds `_version`.
 * Used by mapped domain models (not DTOs) that carry a version token.
 */
export interface ViewRevision extends Auditable {
  _version: string;
}

/**
 * Variant of {@link ViewRevision} for domain models where `createdBy`/`updatedBy`
 * are full {@link RevisionBy} objects sourced from `_embedded`, rather than plain
 * {@link UserId} strings. Use this when the mapper promotes embedded user objects
 * directly onto the domain model (e.g. `Account`).
 */
export interface ViewRevisionEmbed {
  createdAt: Date;
  updatedAt: Date | null;
  createdBy: RevisionBy;
  updatedBy: RevisionBy | null;
  _version: string;
}

export type RevisionByUser = {
  id: UserId;
  _version: string;
  _type: 'user';
  firstName: string;
  lastName: string;
};

export type RevisionByClient = {
  id: UserId;
  _version: string;
  _type: 'client';
  name: RevisionByClientName;
};

export type RevisionBy = RevisionByUser | RevisionByClient;

export enum RevisionByClientName {
  JoinImporter = 'marketplace-apis_join-importer',
  CustomerFeed = 'marketplace-apis_customer-feed',
}

export interface RevisionEmbed {
  _embedded: {
    createdBy: Readonly<RevisionBy>;
    updatedBy: Readonly<RevisionBy | null>;
  };
}

/**
 * Client means that the revision was done by JobCloud, here the casting is implicit,
 * if it contains `{ _type : client }`, it will necessarily have `{ name : RevisionByClientName }`.
 * @param revisionBy
 */
export const isRevisionByClient = (revisionBy: RevisionBy): revisionBy is RevisionByClient =>
  revisionBy._type === 'client';

export const isRevisionByUser = (revisionBy: RevisionBy): revisionBy is RevisionByUser =>
  revisionBy._type === 'user';
